import { Store } from '@ngrx/store';
import { Observable, Subscription, zip } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { TranslationService } from '@cemex-core/angular-localization-v7';
import { PaymentHeader } from '@cemex-core/types-v1';

import { PaymentEngine } from './payment-engine';
import { PaymentConfiguration } from './../models/payment-configuration';
import { PaymentMethod } from './../models/payment-method';
import { ParametersService } from './../../core/services/parameters.service';
import { ParameterGroup, Parameter } from './../../core/models/parameter.model';
import { Customer } from './../../core/models/customer.model';
import { Company } from './../../core/models/company.model';
import { ValidatorFn, Validators } from '@angular/forms';
import { NumberValidators } from './../../../cemexcomponents/helpers/v1';
import { BankService } from './bank.service';
import { AppConfigurationService } from './../../../cemexng2components/components/app-configuration';
import { PaymentEngineConfiguration } from './payment-engine-configuration';
import { PaymentService } from './payment.service';
import { PaymentRequest } from './../models/payment-request';
import { PayerBrokerRequest } from './../models/payer-broker-request';
import { PayerBrokerResponse } from './../models/payer-broker-response';
import * as fromPayments from './../store/reducers/index';

export abstract class AbstractPaymentEngine implements PaymentEngine {

    private featureService: AppConfigurationService;
    private parametersService: ParametersService;
    customer: Customer;
    company: Company;
    bankService: BankService;
    paymentService: PaymentService;
    store: Store<fromPayments.State>;

    paymentConfiguration: Observable<PaymentConfiguration>;
    subscription: Subscription = new Subscription();
    filterPaymentMethodsFeature: any[] = [];
    filterBanksFeature: any[] = [];
    filterExternalCompaniesFeature: any; // this.featureService.getConfiguration('payments.filter-external-companies')
    defaultPaymentCompany: any; // this.featureService.getConfiguration('payments.default-payment-company')
    changeLogoForTextFeature = false;
    disabledBankList = false;
    disableCompanyCustomer = false;    
    
    constructor(protected configuration: PaymentEngineConfiguration, protected t: TranslationService) {
        this.featureService = configuration.initProperties.get('featureService') as AppConfigurationService;
        this.parametersService = configuration.initProperties.get('parametersService') as ParametersService;
        this.bankService = configuration.initProperties.get('bankService') as BankService;
        this.paymentService = configuration.initProperties.get('paymentService') as PaymentService;
        this.customer = configuration.initProperties.get('customer') as Customer;
        this.company = configuration.initProperties.get('company') as Company;
        this.store = configuration.initProperties.get('store$') as Store<fromPayments.State>;

        this.getFeatures();
    }

    getAmountValidators(isDebit: boolean, expectedPayment: number): ValidatorFn | ValidatorFn[] {
        return [ Validators.required,
            NumberValidators.isNumeric, NumberValidators.isPositive, NumberValidators.onlyTwoDecimals,
            // NumberValidators.greaterThanZero,
            NumberValidators.greaterThan(1), NumberValidators.lessThan(expectedPayment) ];
    }

    getDocumentsValidators(): ValidatorFn | ValidatorFn[] {
        return [ Validators.required ];
    }

    abstract getBanks(): any;
    abstract getPaymentConfiguration(): Observable<PaymentConfiguration>;
    abstract createReference(request: PaymentRequest): Observable<any>;
    abstract getPayerBroker(request: PayerBrokerRequest, brokerCode: string): Observable<any>;
    abstract callToNetworking(totalToPay: string, referenceCode: string, payerInformation: PayerBrokerResponse): void;
    abstract saveExternalOrderRelation(externalOrder: string, reference: string, bankCode: string, countryCode: string, customerCode: string, companyCode: string): Observable<any>;
    abstract getHeaderPayerInformation(request: any, payerInformationResponse: any, storeDataInfo: any): any;

    initPaymentConfiguration(): Observable<PaymentConfiguration> {
        return zip(
            this.parametersService.getCountryParameterGroups(this.customer.countryCode),
            this.parametersService.getCustomerParameterGroups(this.customer.customerCode, this.customer.countryCode)
        ).pipe(
            filter(([countryParameters, customerParameters]) => countryParameters !== undefined && customerParameters !== undefined),
            map(([countryParameters, customerParameters]) => {
                let paymentConfiguration: PaymentConfiguration;

                const countryParams = countryParameters.filter(param => param.parameterGroup.toLocaleUpperCase() === 'COUNTRY');
                if (countryParams.length > 0) {
                    paymentConfiguration = this.setCountryParameters(countryParams[0].parameters);
                }

                const countryMethodsParams = countryParameters.filter(param => param.parameterGroup.toLocaleUpperCase() !== 'COUNTRY');
                paymentConfiguration.paymentMethods = this.setCountryPaymentMethods(countryMethodsParams);

                const customerParams = customerParameters.filter(param => param.parameterGroup.toLocaleUpperCase() === 'CUST');
                if (customerParams.length > 0) {
                    paymentConfiguration = this.setCustomerParameters(paymentConfiguration, customerParams[0].parameters);
                }

                const customerMethodsParams = customerParameters.filter(param => param.parameterGroup.toLocaleUpperCase() !== 'CUST');
                paymentConfiguration.paymentMethods =
                    this.setCustomerPaymentMethods(paymentConfiguration.paymentMethods, customerMethodsParams);

                return paymentConfiguration;
            })
        );
    }

    createFormRequestNetwork(action: string, method: string, target: string, parameters?: { [key: string]: any }): void {
        const form = document.createElement('form');

        if (parameters !== undefined) {
            Object.keys(parameters).forEach(key => {
                form.append(this.createFormInput(key, parameters[key]));
            });
        }

        form.method = method;
        form.target = target;
        form.action = action;

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }

    decodeParameters(formParams: any[]): any[] {
        const parameters = [];

        formParams.forEach((param) => {
            parameters[param.key] = atob(param.value);
        });

        return parameters;
    }

    private createFormInput(name: string, value: any): HTMLInputElement {
        const input = document.createElement('input');
        input.name = name;
        input.value = value;
        input.type = 'hidden';

        return input;
    }

    /// Get all features related to payments
    private getFeatures(): void {
        /// Load payment methods allowed by system
        if (this.featureService.isEnabled('payments.filter-payment-methods')) {
            this.filterPaymentMethodsFeature = this.featureService.getConfiguration('payments.filter-payment-methods').options;
        }

        /// List of banks to filter
        if (this.featureService.isEnabled('payments.filter-banks')) {
            this.filterBanksFeature = this.featureService.getConfiguration('payments.filter-banks').options;
        }

        /// Set configuration form company and system
        if (this.featureService.isEnabled('payments.filter-external-companies')) {
            this.filterExternalCompaniesFeature = this.featureService.getConfiguration('payments.filter-external-companies').options;
        }

        /// Set default company by country 
        if (this.featureService.isEnabled('payments.default-payment-company')) {
           this.defaultPaymentCompany = this.featureService.getConfiguration('payments.default-payment-company').options;
        }

        this.changeLogoForTextFeature = this.featureService.isEnabled('payments.change-logo-for-text');
        this.disabledBankList = this.featureService.isEnabled('payments.disable-banklist-to-transfer-method');
        this.disableCompanyCustomer = this.featureService.isEnabled('payments.disable-company-customer-select');
    }

    /// Set country parameters definitions
    private setCountryParameters(countryParameters: Parameter[]): PaymentConfiguration {
        return {
            allowPartialPayment: (this.getParameterValue(countryParameters, 'PRTPAY', '0') === '1'),
            allowReconcile: (this.getParameterValue(countryParameters, 'CLRINV', '0') === '1'),
            allowMultiDocumentsPayment: (this.getParameterValue(countryParameters, 'MULTPAY', '0') === '1'),
            allowMultiCompanyPayment: (this.getParameterValue(countryParameters, 'MULTPAYENT', '0') === '1'),
            allowMultiCompanyReconcile: (this.getParameterValue(countryParameters, 'MULTCLRENT', '0') === '1'),
            requiredReasonPartialPayment: (this.getParameterValue(countryParameters, 'REASONCODE', '0') === '1'),
            validateDueDocumentsDiscount: (this.getParameterValue(countryParameters, 'DUEDOCS', '0') === '1'),
            enabledDiscount: (this.getParameterValue(countryParameters, 'ENABLEPPD', '0') === '1'),
            changeLogoForText: this.changeLogoForTextFeature,
            disabledBankList: this.disabledBankList,
            disableCompanyCustomer: this.disableCompanyCustomer,
            defaultCompany: this.getParameterValue(countryParameters, 'DEF_COMPANY_FILTER', '0'),
            externalCompanyDefault: this.filterExternalCompaniesFeature.companies[0],
            defaultPaymentCompanyFeature: this.defaultPaymentCompany.companies[0],
            paymentMethods: []
        };
    }

    /// Set country definition from backend
    private setCountryPaymentMethods(paymentMethods: ParameterGroup[]): PaymentMethod[] {
        const methods = [];

        if (this.filterPaymentMethodsFeature !== undefined && this.filterPaymentMethodsFeature.length > 0) {
            const filterCompany = this.filterPaymentMethodsFeature.filter(x => x.companies.includes(this.company.companyCode));
            if (filterCompany.length > 0) {
                const filterPayMethods = filterCompany.reduce(function(p, c) {
                    p = p.concat(c.paymentMethods);
                    return p;
                }, []);
                paymentMethods = paymentMethods.filter(p => filterPayMethods.includes(p.parameterGroup));
            }
        }

        paymentMethods.forEach(paymentMethod => {
            methods.push({
                code: paymentMethod.parameterGroup,
                shortCode: this.getShortCode(paymentMethod.parameterGroup),
                description: paymentMethod.parameterDesc,
                minAmount: +this.getParameterValue(paymentMethod.parameters, 'MINAMT', 0),
                maxAmount: +this.getParameterValue(paymentMethod.parameters, 'MAXAMT', 0),
                fee: +this.getParameterValue(paymentMethod.parameters, 'FEE', 0),
                displayIcon: 'cmx-icon-bank-card',
                displayDescription: '',
                displayName: '',
                enableRestrictionHour: +this.getParameterBooleanValue(paymentMethod.parameters, 'ENABLESCHEDULE', 'false'),
                hourFrom: +this.getParameterValue(paymentMethod.parameters, 'HOURFROM', 0),
                hourTo: +this.getParameterValue(paymentMethod.parameters, 'HOURTO', 0),
                disable: false
            });
        });

        return methods;
    }

    /// Set customer definition from backend
    private setCustomerParameters(paymentConfiguration: PaymentConfiguration, customerParameters: Parameter[]): PaymentConfiguration {
        paymentConfiguration.allowPartialPayment =
            (this.getParameterValue(customerParameters, 'PRTPAY', paymentConfiguration.allowPartialPayment ? '1' : '0') === '1');
        paymentConfiguration.allowReconcile =
            (this.getParameterValue(customerParameters, 'CLRINV', paymentConfiguration.allowReconcile ? '1' : '0') === '1');
        paymentConfiguration.allowMultiDocumentsPayment =
            (this.getParameterValue(customerParameters, 'MULTPAY', paymentConfiguration.allowMultiDocumentsPayment ? '1' : '0')
                === '1');
        paymentConfiguration.allowMultiCompanyPayment =
            (this.getParameterValue(customerParameters, 'MULTPAYENT', paymentConfiguration.allowMultiCompanyPayment ? '1' : '0')
                === '1');
        paymentConfiguration.allowMultiCompanyReconcile =
            (this.getParameterValue(customerParameters, 'MULTCLRENT', paymentConfiguration.allowMultiCompanyReconcile ? '1' : '0')
                === '1');
        paymentConfiguration.requiredReasonPartialPayment =
            (this.getParameterValue(customerParameters, 'REASONCODE', paymentConfiguration.requiredReasonPartialPayment ? '1' : '0')
                === '1');
        paymentConfiguration.validateDueDocumentsDiscount =
            (this.getParameterValue(customerParameters, 'DUEDOCS', paymentConfiguration.validateDueDocumentsDiscount ? '1' : '0')
            === '1');
        paymentConfiguration.enabledDiscount =
            (this.getParameterValue(customerParameters, 'ENABLEPPD', paymentConfiguration.enabledDiscount ? '1' : '0') === '1');

        return paymentConfiguration;
    }

    /// Set customer payment methods definition from backend
    private setCustomerPaymentMethods(paymentMethods: PaymentMethod[], customerPaymentMethods: ParameterGroup[]): PaymentMethod[] {
        const methods = Object.assign([], paymentMethods);

        customerPaymentMethods.forEach(paymentMethod => {
            const index = paymentMethods.findIndex(x => x.code === paymentMethod.parameterGroup);

            if (index === -1) {
                methods.push({
                    code: paymentMethod.parameterGroup,
                    shortCode: this.getShortCode(paymentMethod.parameterGroup),
                    description: paymentMethod.parameterDesc,
                    minAmount: +this.getParameterValue(paymentMethod.parameters, 'CUSTMINAMT', 0),
                    maxAmount: +this.getParameterValue(paymentMethod.parameters, 'CUSTMAXAMT', 0),
                    fee: +this.getParameterValue(paymentMethod.parameters, 'FEE', 0),
                    displayIcon: 'cmx-icon-bank-card',
                    displayDescription: '',
                    displayName: '',
                    enableRestrictionHour: +this.getParameterBooleanValue(paymentMethod.parameters, 'ENABLESCHEDULE', 'false'),
                    hourFrom: +this.getParameterValue(paymentMethod.parameters, 'HOURFROM', 0),
                    hourTo: +this.getParameterValue(paymentMethod.parameters, 'HOURTO', 0),
                    disable: false,
                });
            } else {
                methods[index].minAmount = +this.getParameterValue(paymentMethod.parameters, 'CUSTMINAMT', 0);
                methods[index].maxAmount = +this.getParameterValue(paymentMethod.parameters, 'CUSTMAXAMT', 0);
                methods[index].fee = +this.getParameterValue(paymentMethod.parameters, 'FEE', 0);
                methods[index].displayIcon = 'cmx-icon-bank-card';
                methods[index].displayDescription = '';
                methods[index].displayName = '';
                methods[index].enableRestrictionHour = +this.getParameterBooleanValue(paymentMethod.parameters, 'ENABLESCHEDULE', 0);
                methods[index].hourFrom = +this.getParameterValue(paymentMethod.parameters, 'HOURFROM', 0);
                methods[index].hourTo = +this.getParameterValue(paymentMethod.parameters, 'HOURTO', 0);
                methods[index].disable = false;
            }
        });

        return methods;
    }

    private getParameterValue(parameters: Parameter[], parameter: string, defaultValue: any): any {
        const params = parameters.filter(param => param.parameterCode.toLocaleUpperCase() === parameter);

        return params.length > 0 ? params[0].value1 : defaultValue;
    }

    private getParameterBooleanValue(parameters: Parameter[], parameter: string, defaultValue: any): boolean {
        const params = parameters.filter(param => param.parameterCode.toLocaleUpperCase() === parameter);
        let aux = params.length > 0 ? params[0].value1 : defaultValue;
        if (aux === 'true'){
            return true;
        }
        return false;
    }

    private getShortCode(code: string): string {
        switch (code.toLocaleUpperCase()) {
            case 'TRANSFER':
                return 'TR';
            case 'CREDIT':
                return 'CC';
            case 'REFERENCE':
                return 'RP';
            case 'CORPORATECREDITCARD':
                return 'CO';
            case 'OTHERCARDS':
                return 'OC';
        }
    }

}
